import themeDefaults from "../theme/theme.tuning.json";
import motionDefaults from "../motion/motion.tuning.json";
import audioDefaults from "../audio/audio.tuning.json";
import rulesDefaults from "../../shared/rules.tuning.json";
import matcherDefaults from "../../shared/matcher.tuning.json";
import { createStore, useStore } from "./store.js";

export type ThemeTuning = typeof themeDefaults;
export type MotionTuning = typeof motionDefaults;
export type AudioTuning = typeof audioDefaults;
export type RulesTuning = typeof rulesDefaults;
export type MatcherTuning = typeof matcherDefaults;

export const TUNING_FILES = {
  theme: "src/theme/theme.tuning.json",
  motion: "src/motion/motion.tuning.json",
  audio: "src/audio/audio.tuning.json",
  rules: "shared/rules.tuning.json",
  matcher: "shared/matcher.tuning.json",
} as const;

export type TuningKey = keyof typeof TUNING_FILES;

export const themeStore = createStore<ThemeTuning>(
  structuredClone(themeDefaults),
);
export const motionStore = createStore<MotionTuning>(
  structuredClone(motionDefaults),
);
export const audioStore = createStore<AudioTuning>(
  structuredClone(audioDefaults),
);
export const rulesStore = createStore<RulesTuning>(
  structuredClone(rulesDefaults),
);
export const matcherStore = createStore<MatcherTuning>(
  structuredClone(matcherDefaults),
);

export const DEFAULTS = {
  theme: themeDefaults,
  motion: motionDefaults,
  audio: audioDefaults,
  rules: rulesDefaults,
  matcher: matcherDefaults,
} as const;

const stores = {
  theme: themeStore,
  motion: motionStore,
  audio: audioStore,
  rules: rulesStore,
  matcher: matcherStore,
} as const;

export const useTheme = () => useStore(themeStore);
export const useMotionTuning = () => useStore(motionStore);
export const useAudioTuning = () => useStore(audioStore);
export const useRules = () => useStore(rulesStore);
export const useMatcherTuning = () => useStore(matcherStore);

/** Pull the on-disk version so a tuned value survives a page reload. */
export async function hydrateTuning(): Promise<void> {
  await Promise.all(
    (Object.keys(TUNING_FILES) as TuningKey[]).map(async (key) => {
      try {
        const res = await fetch(`/api/tuning/${TUNING_FILES[key]}`);
        if (!res.ok) return;
        stores[key].set(await res.json());
      } catch {
        // Server down or offline build — the bundled defaults already loaded.
      }
    }),
  );
}

export type SaveState = {
  status: "idle" | "saving" | "saved" | "error";
  at?: number;
  error?: string;
};
const saveStores = {
  theme: createStore<SaveState>({ status: "idle" }),
  motion: createStore<SaveState>({ status: "idle" }),
  audio: createStore<SaveState>({ status: "idle" }),
  rules: createStore<SaveState>({ status: "idle" }),
  matcher: createStore<SaveState>({ status: "idle" }),
};
export const useSaveState = (key: TuningKey) => useStore(saveStores[key]);

const timers = new Map<TuningKey, ReturnType<typeof setTimeout>>();

/**
 * Update live, then persist to the JSON file on disk. Browser storage is
 * deliberately not involved: the file is the record, so a tuning session shows
 * up in `git diff` and survives the dev server dying.
 */
export function updateTuning<K extends TuningKey>(
  key: K,
  next: K extends "theme"
    ? ThemeTuning
    : K extends "motion"
      ? MotionTuning
      : K extends "audio"
        ? AudioTuning
        : K extends "rules"
          ? RulesTuning
          : MatcherTuning,
): void {
  (stores[key] as { set(v: unknown): void }).set(next);
  clearTimeout(timers.get(key));
  timers.set(
    key,
    setTimeout(async () => {
      saveStores[key].set({ status: "saving" });
      try {
        const res = await fetch("/__tuner/save", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            file: TUNING_FILES[key],
            value: stores[key].get(),
          }),
        });
        if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
        saveStores[key].set({ status: "saved", at: Date.now() });
      } catch (e) {
        saveStores[key].set({ status: "error", error: (e as Error).message });
      }
    }, 400),
  );
}

export function resetTuning(key: TuningKey): void {
  updateTuning(key, structuredClone(DEFAULTS[key]) as never);
}

/**
 * Project theme tokens onto CSS custom properties. Everything visual reads
 * these, so one tuner drag repaints the whole app with no re-render.
 */
export function applyThemeToDocument(theme: ThemeTuning): void {
  const root = document.documentElement;
  const set = (name: string, value: string | number) =>
    root.style.setProperty(name, String(value));

  for (const [key, value] of Object.entries(theme.palette))
    set(`--c-${kebab(key)}`, value);
  for (const [key, value] of Object.entries(theme.radius))
    set(`--r-${kebab(key)}`, `${value}px`);
  for (const [key, value] of Object.entries(theme.type)) {
    const suffix =
      typeof value === "number" &&
      !key.includes("Weight") &&
      !key.includes("Tracking");
    set(`--t-${kebab(key)}`, suffix ? `${value}px` : String(value));
  }
  for (const [key, value] of Object.entries(theme.board)) {
    set(
      `--b-${kebab(key)}`,
      typeof value === "number" && key !== "columns" ? `${value}px` : value,
    );
  }

  const p = theme.print;
  set("--stroke", `${p.strokeWidth}px`);
  set("--stroke-heavy", `${p.strokeWidthHeavy}px`);
  // Zero blur is the whole point: a blurred shadow reads as a UI card, a hard
  // offset reads as ink printed slightly off-register on paper.
  set("--drop", `${p.shadowOffsetX}px ${p.shadowOffsetY}px 0`);
  set("--drop-lift", `${p.shadowOffsetX}px ${p.shadowOffsetLift}px 0`);
  set("--drop-alpha", p.shadowAlpha);
  set("--riso-x", `${p.risoOffsetX}px`);
  set("--riso-y", `${p.risoOffsetY}px`);
  set("--riso-opacity", p.risoOpacity);
  set("--tilt", `${p.stickerTiltDeg}deg`);
  set("--perf-size", `${p.perforationSize}px`);
  set("--perf-gap", `${p.perforationGap}px`);

  set("--t-roster-size", `${theme.type.rosterSize}px`);
  set("--t-chip-size", `${theme.type.chipSize}px`);
  set("--t-subtitle-size", `${theme.type.subtitleSize}px`);

  set("--nextup-height", `${theme.nextUp.barHeight}px`);
  set("--nextup-label-size", `${theme.nextUp.labelSize}px`);

  set("--grain-opacity", theme.grain.opacity);
  set("--grain-scale", `${theme.grain.scale}px`);
}

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}
